import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CustomerTestimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      location: "Downtown Regular",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      rating: 5,
      date: "2 days ago",
      review: `Kebab Palace has become our family's go-to spot for authentic Mediterranean food. The lamb shawarma is absolutely incredible - you can taste the love and tradition in every bite. The staff treats us like family, and my kids always ask when we're going back!

The quality is consistently outstanding, and I love that I can watch them prepare our food through their open kitchen. It's clear they take pride in what they do.`,
      orderHistory: "47 orders",
      favoriteItem: "Lamb Shawarma Platter",
      verified: true,
      helpful: 23
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      location: "Midtown Food Enthusiast",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      rating: 5,
      date: "1 week ago",
      review: `As someone who's traveled extensively through the Mediterranean, I can confidently say that Kebab Palace serves the most authentic food I've found outside of the region. Chef Omar's attention to traditional techniques is remarkable.

The mixed grill supreme is a masterpiece - perfectly seasoned, expertly grilled, and served with sides that complement each other beautifully. This isn't just fast food; it's culinary artistry.`,
      orderHistory: "31 orders",
      favoriteItem: "Mixed Grill Supreme",
      verified: true,
      helpful: 18
    },
    {
      id: 3,
      name: "Emily Chen",
      location: "Health-Conscious Foodie",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      rating: 5,
      date: "3 days ago",
      review: `I'm always looking for healthy, fresh options, and Kebab Palace delivers every time. Their falafel garden bowl is packed with nutrients and flavor - it's become my weekly treat. I appreciate their transparency about ingredients and preparation methods.

The fact that everything is made fresh daily really shows in the taste and quality. Plus, their staff is incredibly knowledgeable about dietary restrictions and always helps me customize my order perfectly.`,
      orderHistory: "22 orders",
      favoriteItem: "Falafel Garden Bowl",
      verified: true,
      helpful: 15
    },
    {
      id: 4,
      name: "David Thompson",
      location: "Business District",
      avatar: "https://randomuser.me/api/portraits/men/38.jpg",
      rating: 5,
      date: "5 days ago",
      review: `Kebab Palace has revolutionized my lunch routine. The online ordering is seamless, the food is always ready on time, and the quality never disappoints. Their chicken kebab feast is my go-to - it's filling, flavorful, and gives me energy for the rest of my workday.

The consistency is what impresses me most. Whether I order on Monday or Friday, the food is always prepared to the same high standard. That's rare in today's restaurant scene.`,
      orderHistory: "38 orders",
      favoriteItem: "Chicken Kebab Feast",
      verified: true,
      helpful: 12
    },
    {
      id: 5,
      name: "Lisa Martinez",
      location: "Family Dining Expert",
      avatar: "https://randomuser.me/api/portraits/women/42.jpg",
      rating: 5,
      date: "1 week ago",
      review: `Finding a restaurant that satisfies both adults and kids can be challenging, but Kebab Palace nails it every time. The Mediterranean mezze selection is perfect for sharing, and my teenagers actually request to eat here - that's saying something! The atmosphere is warm and welcoming, and the staff goes out of their way to accommodate our large family orders. It's become our celebration spot for birthdays and achievements.`,
      orderHistory: "29 orders",
      favoriteItem: "Mediterranean Mezze Selection",
      verified: true,
      helpful: 20
    }
  ];

  const overallStats = {
    averageRating: 4.8,
    totalReviews: 1247,
    fiveStarPercentage: 89,
    recommendationRate: 96
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? 'text-accent fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            What Our Family Says
          </h2>
          <p className="font-body text-xl text-text-secondary max-w-3xl mx-auto mb-8">
            Real stories from real customers who've made Kebab Palace part of their dining tradition
          </p>
          
          {/* Overall Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-2">
                {renderStars(5)}
              </div>
              <div className="font-cta font-bold text-2xl text-text-primary">
                {overallStats.averageRating}/5
              </div>
              <div className="text-sm text-text-secondary">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="font-cta font-bold text-2xl text-primary mb-1">
                {overallStats.totalReviews.toLocaleString()}
              </div>
              <div className="text-sm text-text-secondary">Total Reviews</div>
            </div>
            <div className="text-center">
              <div className="font-cta font-bold text-2xl text-accent mb-1">
                {overallStats.fiveStarPercentage}%
              </div>
              <div className="text-sm text-text-secondary">5-Star Reviews</div>
            </div>
            <div className="text-center">
              <div className="font-cta font-bold text-2xl text-trust mb-1">
                {overallStats.recommendationRate}%
              </div>
              <div className="text-sm text-text-secondary">Would Recommend</div>
            </div>
          </div>
        </div>

        {/* Main Testimonial Display */}
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-surface rounded-2xl shadow-warm-lg overflow-hidden">
            {/* Testimonial Content */}
            <div className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                {/* Customer Info */}
                <div className="lg:col-span-1">
                  <div className="text-center lg:text-left">
                    <div className="relative inline-block mb-4">
                      <Image
                        src={testimonials[currentTestimonial].avatar}
                        alt={testimonials[currentTestimonial].name}
                        className="w-20 h-20 rounded-full object-cover mx-auto lg:mx-0"
                      />
                      {testimonials[currentTestimonial].verified && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full flex items-center justify-center">
                          <Icon name="Check" size={14} className="text-white" />
                        </div>
                      )}
                    </div>
                    
                    <h3 className="font-cta font-bold text-xl text-text-primary mb-1">
                      {testimonials[currentTestimonial].name}
                    </h3>
                    <p className="text-text-secondary mb-3">
                      {testimonials[currentTestimonial].location}
                    </p>
                    
                    <div className="flex items-center justify-center lg:justify-start space-x-1 mb-4">
                      {renderStars(testimonials[currentTestimonial].rating)}
                    </div>
                    
                    <div className="space-y-2 text-sm text-text-secondary">
                      <div className="flex items-center justify-center lg:justify-start space-x-2">
                        <Icon name="ShoppingBag" size={14} />
                        <span>{testimonials[currentTestimonial].orderHistory}</span>
                      </div>
                      <div className="flex items-center justify-center lg:justify-start space-x-2">
                        <Icon name="Heart" size={14} />
                        <span>{testimonials[currentTestimonial].favoriteItem}</span>
                      </div>
                      <div className="flex items-center justify-center lg:justify-start space-x-2">
                        <Icon name="Calendar" size={14} />
                        <span>{testimonials[currentTestimonial].date}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Review Content */}
                <div className="lg:col-span-2">
                  <div className="relative">
                    <Icon name="Quote" size={48} className="text-primary-200 absolute -top-4 -left-2" />
                    <div className="font-body text-lg text-text-secondary leading-relaxed pl-8">
                      <p className="mb-4">{testimonials[currentTestimonial].review}</p>
                    </div>
                  </div>
                  
                  {/* Review Actions */}
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-primary-100">
                    <div className="flex items-center space-x-4">
                      <button className="flex items-center space-x-2 text-text-secondary hover:text-primary transition-organic">
                        <Icon name="ThumbsUp" size={16} />
                        <span className="text-sm">Helpful ({testimonials[currentTestimonial].helpful})</span>
                      </button>
                      <button className="flex items-center space-x-2 text-text-secondary hover:text-primary transition-organic">
                        <Icon name="Share" size={16} />
                        <span className="text-sm">Share</span>
                      </button>
                    </div>
                    
                    {testimonials[currentTestimonial].verified && (
                      <div className="flex items-center space-x-2 text-success">
                        <Icon name="Shield" size={16} />
                        <span className="text-sm font-medium">Verified Customer</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial Navigation */}
          <div className="flex justify-center mt-8 space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToTestimonial(index)}
                className={`w-4 h-4 rounded-full transition-organic ${
                  index === currentTestimonial 
                    ? 'bg-primary shadow-warm' 
                    : 'bg-primary-200 hover:bg-primary-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Additional Testimonials Grid */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <div key={testimonial.id} className="card-surface">
              <div className="flex items-center space-x-3 mb-4">
                <Image
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-cta font-semibold text-text-primary">
                    {testimonial.name}
                  </h4>
                  <div className="flex items-center space-x-1">
                    {renderStars(testimonial.rating)}
                  </div>
                </div>
              </div>
              <p className="text-text-secondary text-sm leading-relaxed">
                {testimonial.review.substring(0, 150)}...
              </p>
              <div className="mt-4 flex items-center justify-between text-xs text-text-secondary">
                <span>{testimonial.favoriteItem}</span>
                <span>{testimonial.date}</span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <p className="font-body text-text-secondary mb-6">
            Join thousands of satisfied customers who've made us their Mediterranean home
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-conversion">
              <Icon name="Star" size={20} className="mr-2" />
              Leave a Review
            </button>
            <button className="btn-primary">
              <Icon name="MessageCircle" size={20} className="mr-2" />
              Read All Reviews
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerTestimonials;